import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import logo from "assets/images/logos/Logo_1.png";
import TnC from "assets/docs/tnc.pdf";
import { signupForm } from "pages/signup/form/Signup_Form";
import CustomForm from "components/widgets/forms/CustomForm";
import SignupSchema from "pages/signup/validations/SignupSchema";

// Styled components for split-screen layout
const SplitScreenContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: ##111827; /* bg-gray-900 */

   @media (max-width: 768px) {
   flex-direction: column;
   height: auto;
   min-height: 100vh;

`;

const LeftPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #1e3a8a, #4f46e5); /* indigo gradient */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 2rem;
  text-align: center;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ##111827;
`;

const SignupCard = styled.div`
  width: 100%;
  max-width: 28rem;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.05); /* slight transparency */
  border-radius: 0.375rem; /* rounded-md */
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.img`
  margin: 0 auto;
  height: 10.5rem;
  width: auto;
`;

const MobileLogo = styled.img`
  margin: 0 auto;
  height: 2.5rem;
  width: auto;
  @media (min-width: 768px) {
    display: none; /* d-md-none equivalent */
  }
`;

const Title = styled.h3`
  margin-top: 2.5rem;
  font-size: 1.5rem;
  line-height: 2.25rem;
  font-weight: 700;
  color: white;
`;

const Description = styled.p`
  margin-top: 1rem;
  color: #f3f4f6; /* text-gray-100 */
  font-size: 1rem;
`;

const BenefitsTitle = styled.h4`
  margin-top: 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
`;

const BenefitsList = styled.ul`
  margin-top: 1rem;
  list-style: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
`;

const BenefitItem = styled.li`
  color: #f3f4f6;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: calc(50% - 0.5rem); /* Two-column layout */
  padding: 0.5rem;
`;

const FooterLinks = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
`;

const FooterLink = styled.a`
  color: #f3f4f6;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const StyledForm = styled.div`
  margin-top: 2.5rem;
  &.form {
    /* Ensure compatibility with CustomForm's className */
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const LoginLink = styled.p`
  text-align: right;
  color: #f3f4f6;
  font-size: 0.875rem;
  margin-top: 1rem;
  a {
    color: #6366f1;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Signup = ({ signup }) => {
  const onSubmit = (model) => {
    signup.mutate(model);
  };

  return (
    <SplitScreenContainer>
      <LeftPanel>
        <Logo src={"https://res.cloudinary.com/doifxgf1h/image/upload/v1768298670/Nexus_Versions_FA-01-removebg-preview_mcak7j.png"} alt="Logo" />
        <Title>Register for Free</Title>
        <Description>
          Nexus Intermediaries is a modern insurance agency platform designed to connect insurance professionals with trusted insurers,
          streamlined processes, and powerful digital tools, all in one place. By registering on Nexus Intermediaries, you’re empowered with a smart, 
          digital platform built for efficiency and growth. From managing portfolios to closing deals faster, everything you need is available in one place.
        </Description>
        <BenefitsTitle>Benefits of being part of us</BenefitsTitle>
        <BenefitsList>
          {[
            "Centralized Portfolio Management for full visibility and control",
            "Digital Distribution to expand reach and reduce manual processes",
            "⁠Simplified Policy Administration that saves time and minimizes errors",
            "⁠Quotation & Leads Management to convert prospects effortlessly",
            "⁠Product Management with access to multiple insurance solutions",
            "⁠Client Management to strengthen relationships and improve service delivery"
          ].map((benefit, index) => (
            <BenefitItem key={index}>
              <i className="fa fa-check pr-2" /> {benefit}
            </BenefitItem>
          ))}
        </BenefitsList>
        <FooterLinks>
          <FooterLink href={TnC} rel="noopener noreferrer" target="_blank">
            Terms and conditions
          </FooterLink>
          <FooterLink as={Link} to="#">
            &copy; {new Date().getFullYear()} Nexus
          </FooterLink>
        </FooterLinks>
      </LeftPanel>
      <RightPanel>
        <SignupCard>
          <MobileLogo src={logo} alt="Logo" className="d-md-none" />
          <StyledForm>
            <CustomForm
              className="form mt-md-0 mt-4"
              validationSchema={SignupSchema}
              model={signupForm}
              onSubmit={(model) => {
                onSubmit(model);
              }}
            />
            <LoginLink>
              Already have an account?{" "}
              <Link to="/login" className="custom-text-primary">
                <small>Log In</small>
              </Link>
            </LoginLink>
          </StyledForm>
        </SignupCard>
      </RightPanel>
    </SplitScreenContainer>
  );
};

export default Signup;